import { forwardRef, useImperativeHandle } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { InputField } from "components/inputs/InputField"

import { usePermissions } from "contexts/PermissionsContext"

import { useIsMounted } from "hooks/useIsMounted"

import { PERMISSIONS } from "constants/permissions"

import { userService } from "services/user/user.service"

import { isAuthorityUnavailableError } from "util/api"

export interface TabHandle {
  handleSubmit: () => void
  isSubmitting: boolean
}

interface CustomBonusTabProps {
  userId: string
  coin?: string
  onSuccess: () => void
  onClose: () => void
}

export const CustomBonusTab = forwardRef<TabHandle, CustomBonusTabProps>(({ userId, coin, onSuccess, onClose }, ref) => {
  const isMounted = useIsMounted()
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })
  const lowWagerAllowed = has(PERMISSIONS.BONUS_LOW_WAGER)

  const formik = useFormik({
    initialValues: {
      amount: 0,
      wagerRequirements: 10,
      daysToExpire: 7
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      amount: Yup.number().required().min(1),
      wagerRequirements: Yup.number()
        .required()
        .min(lowWagerAllowed ? 1 : 30),
      daysToExpire: Yup.number().required().min(1)
    }),
    onSubmit: async (values, helpers) => {
      try {
        const { daysToExpire, amount, wagerRequirements } = values
        await userService.claimCustomBonus({
          daysToExpire,
          amount,
          wagerRequirements,
          userId
        })
        toast.success(String(t("addUserBonus.bonusAddedSuccess")))
        onSuccess()
        onClose()
      } catch (err: any) {
        // SOFT-556: a 503 means bonus-service is down and the grant was fail-closed (no phantom
        // bonus) — show a retryable message, not the generic failure. The "already has an active
        // bonus" 400 keeps surfacing its backend message via `err.data.message` below.
        if (isAuthorityUnavailableError(err)) {
          toast.error(String(t("addUserBonus.bonusServiceUnavailable")))
        } else {
          toast.error(err?.data?.message ?? err?.message ?? t("addUserBonus.bonusAddedFailed"))
        }
        if (isMounted()) {
          helpers.setStatus({ success: false })
          helpers.setSubmitting(false)
        }
      }
    }
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: () => formik.handleSubmit(),
    isSubmitting: formik.isSubmitting
  }))

  const fields = [
    {
      name: "amount",
      title: t("addUserBonus.bonusAmount"),
      // No coin resolved → state the amount without a currency rather than assert the wrong one.
      altLabelOne: coin ? `${formik.values.amount / 100} ${coin}` : String(formik.values.amount / 100)
    },
    { name: "wagerRequirements", title: t("addUserBonus.wagerRequirements") },
    { name: "daysToExpire", title: t("addUserBonus.daysToExpire") }
  ] as const

  return (
    <>
      {fields.map(({ name, title, ...rest }) => (
        <InputField
          key={name}
          name={name}
          type="number"
          title={title}
          value={formik.values[name]}
          onChange={formik.handleChange}
          invalid={!!(formik.errors[name] && formik.touched[name])}
          altLabelTwo={formik.touched[name] ? formik.errors[name] : ""}
          {...rest}
        />
      ))}
    </>
  )
})

CustomBonusTab.displayName = "CustomBonusTab"
